import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Menu, Shield, LayoutDashboard, Mail, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const linkCls = ({ isActive }) =>
  `flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive ? "bg-[#0F2D5E]/10 text-[#0F2D5E]" : "text-[#495850] hover:text-[#0F2D5E]"}`;

const NavLinks = ({ user, onClick }) => (
  <>
    {user?.role === "company" && (
      <NavLink to="/azienda" className={linkCls} onClick={onClick} data-testid="nav-azienda">
        <Building2 className="h-4 w-4" /> Area azienda
      </NavLink>
    )}
    {user && user.role !== "company" && (
      <NavLink to="/dashboard" className={linkCls} onClick={onClick} data-testid="nav-dashboard">
        <LayoutDashboard className="h-4 w-4" /> Le mie segnalazioni
      </NavLink>
    )}
    {user?.role === "admin" && (
      <NavLink to="/admin" className={linkCls} onClick={onClick} data-testid="nav-admin">
        <Shield className="h-4 w-4" /> Admin
      </NavLink>
    )}
    <NavLink to="/contatti" className={linkCls} onClick={onClick} data-testid="nav-contatti">
      <Mail className="h-4 w-4" /> Contatti
    </NavLink>
  </>
);

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  const authArea = user ? (
    <Button variant="ghost" onClick={doLogout} className="rounded-full" data-testid="logout-btn">
      <LogOut className="mr-2 h-4 w-4" /> Esci
    </Button>
  ) : (
    <div className="flex flex-col gap-2 md:flex-row">
      <Button variant="ghost" asChild className="rounded-full" data-testid="nav-login">
        <Link to="/accedi" onClick={() => setOpen(false)}>Accedi</Link>
      </Button>
      <Button asChild className="rounded-full bg-[#F4C20D] text-[#0F2D5E] hover:bg-[#E0B000]" data-testid="nav-register">
        <Link to="/registrati" onClick={() => setOpen(false)}>Registrati</Link>
      </Button>
    </div>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-[#E2E5DC] bg-white/90 backdrop-blur-md" data-testid="navbar">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 md:h-20">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex">
          <NavLinks user={user} />
          {user && <span className="ml-3 max-w-[160px] truncate text-sm text-[#75867E]" data-testid="nav-user-name">{user.name}</span>}
          <div className="ml-2">{authArea}</div>
        </nav>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" className="h-11 w-11" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="right" className="flex w-72 flex-col gap-2 bg-white pt-12">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            {user && <p className="px-4 pb-2 text-sm text-[#75867E]">{user.name}</p>}
            <NavLinks user={user} onClick={() => setOpen(false)} />
            <div className="mt-4 border-t pt-4">{authArea}</div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
